<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page"><span>HR Violation Actions</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div v-if="hasViewPermission" class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <h3 class="mb-0">
                            Violations requiring HR actions
                            <strong v-if="selectedViolationId">
                                ({{ filteredViolationCounts[selectedViolationId]?.count || 0 }} of {{
                                    filteredViolationCounts.all.count }})
                            </strong>
                            <strong v-else>
                                (Total: {{ filteredViolationCounts.all.count }} records)
                            </strong>
                        </h3>

                        <!-- Filter controls -->
                        <div class="report-header mt-3">
                            <div class="filter-row row g-2 mb-2">
                                <div class="col-12 col-md-6 col-lg-3">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>From:</label>
                                        <input type="date" v-model="startDate" class="form-control">
                                    </div>
                                </div>
                                <div class="col-12 col-md-6 col-lg-3">
                                    <div class="d-flex align-items-center gap-2">
                                        <label>To:</label>
                                        <input type="date" v-model="endDate" class="form-control">
                                    </div>
                                </div>
                                <div class="col-12 col-md-6 col-lg-3">
                                    <select v-model="selectedShift" class="form-select">
                                        <option value="">All Shifts</option>
                                        <option value="day">Day Shift (08:30-20:30)</option>
                                        <option value="night">Night Shift (20:30-08:30)</option>
                                    </select>
                                </div>
                                <div class="col-12 col-md-6 col-lg-3">
                                    <button class="btn btn-danger w-100" @click="clearFilters">
                                        Clear Filters
                                    </button>
                                </div>
                            </div>

                            <div class="filter-row row g-2 mb-3">
                                <div class="col-12 col-md-9">
                                    <input type="text" v-model="searchValue"
                                        placeholder="Search by driver, vehicle, route..." class="form-control">
                                </div>
                                <div class="col-12 col-md-3">
                                    <button
                                        class="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                                        @click="showPrintModal">
                                        Print Report
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round"
                                            class="feather feather-printer">
                                            <polyline points="6 9 6 2 18 2 18 9"></polyline>
                                            <path
                                                d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2">
                                            </path>
                                            <rect x="6" y="14" width="12" height="8"></rect>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="violation-summary mb-3">
                            <div class="d-flex flex-wrap gap-2">
                                <button class="btn btn-sm"
                                    :class="!selectedViolationId ? 'btn-primary' : 'btn-outline-primary'"
                                    @click="selectedViolationId = ''">
                                    All ({{ filteredViolationCounts.all.count }})
                                </button>
                                <button v-for="violation in violations" :key="violation.id" class="btn btn-sm"
                                    :class="selectedViolationId == violation.id ? 'btn-info' : 'btn-outline-info'"
                                    @click="selectedViolationId = violation.id">
                                    {{ violation.title }} ({{ filteredViolationCounts[violation.id]?.count || 0 }})
                                </button>
                            </div>

                            <div class="d-flex flex-wrap gap-2 mt-2">
                                <button class="btn btn-sm"
                                    :class="!selectedShift ? 'btn-secondary' : 'btn-outline-secondary'"
                                    @click="selectedShift = ''">
                                    All Shifts ({{ shiftCounts.all }})
                                </button>
                                <button class="btn btn-sm"
                                    :class="selectedShift === 'day' ? 'btn-warning' : 'btn-outline-warning'"
                                    @click="selectedShift = 'day'">
                                    Day Shift ({{ shiftCounts.day }})
                                </button>
                                <button class="btn btn-sm"
                                    :class="selectedShift === 'night' ? 'btn-dark' : 'btn-outline-dark'"
                                    @click="selectedShift = 'night'">
                                    Night Shift ({{ shiftCounts.night }})
                                </button>
                            </div>
                        </div>

                        <!-- Items per page selector -->
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <div class="d-flex align-items-center">
                                <span class="me-2">Show:</span>
                                <select v-model="itemsPerPage" class="form-select form-select-sm" style="width: 80px;">
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <span class="ms-2">entries</span>
                            </div>
                            <div class="text-muted">
                                Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                                {{ Math.min(currentPage * itemsPerPage, filteredActions.length) }} of
                                {{ filteredActions.length }} entries
                            </div>
                        </div>

                        <div class="table-responsive">
                            <div v-if="isLoading" class="text-center py-4">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                            </div>

                            <template v-else>
                                <table class="table table-hover table-bordered">
                                    <thead>
                                        <tr>
                                            <th>S/N</th>
                                            <th>Bus</th>
                                            <th>Driver</th>
                                            <th>Conductor</th>
                                            <th>Route</th>
                                            <th>Schedule</th>
                                            <th>Details</th>
                                            <th>Reported By</th>
                                            <th>Date</th>
                                            <th>Status</th>
                                            <th class="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(action, index) in paginatedActions" :key="action.id">
                                            <td>{{ (currentPage - 1) * itemsPerPage + index + 1 }}</td>
                                            <td>{{ action.bus_registration }}</td>
                                            <td>
                                                <router-link to="#"
                                                    @click.prevent="showDriverViolationActionsModal(action.driver_id)">
                                                    {{ getDriverName(action.driver_id) }}
                                                </router-link>
                                            </td>
                                            <td>{{ action.conductor_id }}</td>
                                            <td>{{ action.route }}</td>
                                            <td>{{ action.schedule }}</td>
                                            <td>
                                                <template v-if="action.parameters">
                                                    <ul class="list-unstyled mb-0">
                                                        <li v-for="(value, key) in action.parameters" :key="key">
                                                            <strong>{{ key }}:</strong> {{ value }}
                                                        </li>
                                                    </ul>
                                                </template>
                                            </td>
                                            <td>{{ action.user?.username || 'N/A' }}</td>
                                            <td>{{ formatDate(action.created_at) }}</td>
                                            <td>
                                                <span :class="`badge badge-${getStatusClass(action.status)}`">
                                                    {{ action.status }}
                                                </span>
                                            </td>
                                            <td class="text-center">
                                                <button @click="viewDetails(action.id)"
                                                    class="btn btn-info btn-sm me-2 mt-2">
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div v-if="filteredActions.length === 0" class="text-center py-4">
                                    <p>No violations found matching your criteria</p>
                                </div>
                            </template>
                        </div>

                        <!-- Pagination -->
                        <div class="paginating-container pagination-solid flex-column align-items-center mt-4">
                            <ul role="menubar" aria-disabled="false" aria-label="Pagination"
                                class="pagination mb-4 b-pagination">
                                <li role="presentation" class="page-item first"
                                    :class="{ disabled: currentPage === 1 }">
                                    <button role="menuitem" type="button" @click="firstPage"
                                        :disabled="currentPage === 1" :tabindex="currentPage === 1 ? -1 : 0"
                                        aria-label="Go to first page" class="page-link">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                                        </svg>
                                    </button>
                                </li>
                                <li role="presentation" class="page-item prev" :class="{ disabled: currentPage === 1 }">
                                    <button role="menuitem" type="button" @click="prevPage"
                                        :disabled="currentPage === 1" :tabindex="currentPage === 1 ? -1 : 0"
                                        aria-label="Go to previous page" class="page-link">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M15 19l-7-7 7-7"></path>
                                        </svg>
                                    </button>
                                </li>
                                <li role="presentation" class="page-item" v-for="page in totalPages" :key="page"
                                    :class="{ active: currentPage === page }">
                                    <button role="menuitemradio" type="button" @click="goToPage(page)"
                                        :aria-label="`Go to page ${page}`" :aria-checked="currentPage === page"
                                        :aria-posinset="page" :aria-setsize="totalPages"
                                        :tabindex="currentPage === page ? 0 : -1" class="page-link">
                                        {{ page }}
                                    </button>
                                </li>
                                <li role="presentation" class="page-item next"
                                    :class="{ disabled: currentPage === totalPages }">
                                    <button role="menuitem" type="button" @click="nextPage"
                                        :disabled="currentPage === totalPages"
                                        :tabindex="currentPage === totalPages ? -1 : 0" aria-label="Go to next page"
                                        class="page-link">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M9 5l7 7-7 7"></path>
                                        </svg>
                                    </button>
                                </li>
                                <li role="presentation" class="page-item last"
                                    :class="{ disabled: currentPage === totalPages }">
                                    <button role="menuitem" type="button" @click="lastPage"
                                        :disabled="currentPage === totalPages"
                                        :tabindex="currentPage === totalPages ? -1 : 0" aria-label="Go to last page"
                                        class="page-link">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M13 5l7 7-7 7M5 5l7 7-7 7">
                                            </path>
                                        </svg>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Print Preview Modal -->
            <div class="modal fade" id="printPreviewModal" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-xl">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Print HR Violation Actions</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>

                        <div class="modal-body">
                            <!-- Filter Controls -->
                            <div class="row g-3 mb-4">
                                <div class="col-md-3">
                                    <label class="form-label">From Date:</label>
                                    <input type="date" class="form-control" v-model="printStartDate" />
                                </div>
                                <div class="col-md-3">
                                    <label class="form-label">To Date:</label>
                                    <input type="date" class="form-control" v-model="printEndDate" />
                                </div>
                                <div class="col-md-2">
                                    <label class="form-label">Shift:</label>
                                    <select class="form-select" v-model="printShift">
                                        <option value="">All Shifts</option>
                                        <option value="day">Day Shift (08:30–20:30)</option>
                                        <option value="night">Night Shift (20:30–08:30)</option>
                                    </select>
                                </div>
                                <div class="col-md-2">
                                    <label class="form-label">Violation:</label>
                                    <select class="form-select" v-model="printSelectedViolationId">
                                        <option value="">All Violations</option>
                                        <option v-for="violation in violations" :key="violation.id"
                                            :value="violation.id">
                                            {{ violation.title }}
                                        </option>
                                    </select>
                                </div>
                                <div class="col-md-2 d-flex align-items-end">
                                    <button class="btn btn-danger w-100" @click="clearPrintFilters">
                                        Clear Filters
                                    </button>
                                </div>
                            </div>

                            <!-- Report Title -->
                            <div class="mb-3 text-center">
                                <h4>
                                    HR Violation Report: {{ printSelectedViolationId ?
                                        getViolationTitle(printSelectedViolationId) :
                                        'All Violations' }}
                                </h4>
                                <p class="mb-0">
                                    <strong>Date Range:</strong>
                                    {{ printStartDate || '...' }} to {{ printEndDate || '...' }}
                                    <span v-if="printShift"> | <strong>Shift:</strong> {{ printShift }}</span>
                                    <span v-if="printSelectedViolationId"> | <strong>Violation:</strong> {{
                                        getViolationTitle(printSelectedViolationId) }}</span>
                                </p>
                            </div>

                            <!-- Report Table -->
                            <div class="table-responsive">
                                <table class="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Bus</th>
                                            <th>Conductor</th>
                                            <th>Route</th>
                                            <th>Schedule</th>
                                            <th>Details</th>
                                            <th>Amount</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(action, index) in filteredPrintData" :key="action.id">
                                            <td>{{ index + 1 }}</td>
                                            <td>{{ action.violation_action.bus_registration }}</td>
                                            <td>{{ action.violation_action.conductor_id }}</td>
                                            <td>{{ action.violation_action.route }}</td>
                                            <td>{{ action.violation_action.schedule }}</td>
                                            <td>
                                                <ul class="list-unstyled mb-0">
                                                    <li v-for="(val, key) in action.violation_action.parameters"
                                                        :key="key">
                                                        <strong>{{ key }}:</strong> {{ val }}
                                                    </li>
                                                </ul>
                                            </td>
                                            <td>{{ action.consolidated_amount ?
                                                formatCurrency(action.consolidated_amount) : 'N/A'
                                                }}
                                            </td>
                                            <td>{{ action.remarks }}</td>
                                        </tr>
                                        <tr v-if="filteredPrintData.length === 0">
                                            <td colspan="9" class="text-center">No data found for selected criteria</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div class="modal-footer">
                            <button class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                            <button class="btn btn-primary" @click="printFilteredHrActions">
                                <i class="fas fa-print"></i> Print
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Driver Violations Modal -->
            <div class="modal fade" id="viewDriverViolationActionsModal" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-xl modal-dialog-scrollable">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">
                                Violations by: <span class="text-primary">{{ getDriverName(selectedDriverId) }}</span>
                            </h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>

                        <div class="modal-body">
                            <!-- Filters -->
                            <div class="row g-3 mb-3">
                                <div class="col-md-3">
                                    <label>From:</label>
                                    <input type="date" v-model="driverViolationFilters.startDate" class="form-control">
                                </div>
                                <div class="col-md-3">
                                    <label>To:</label>
                                    <input type="date" v-model="driverViolationFilters.endDate" class="form-control">
                                </div>
                                <div class="col-md-2">
                                    <label>Shift:</label>
                                    <select class="form-select" v-model="driverViolationFilters.shift">
                                        <option value="">All</option>
                                        <option value="day">Day (08:30–20:30)</option>
                                        <option value="night">Night (20:30–08:30)</option>
                                    </select>
                                </div>
                                <div class="col-md-2">
                                    <label>Violation:</label>
                                    <select class="form-select" v-model="driverViolationFilters.violationId">
                                        <option value="">All</option>
                                        <option v-for="v in violations" :value="v.id" :key="v.id">{{ v.title }}</option>
                                    </select>
                                </div>
                                <div class="col-md-2">
                                    <label>Status:</label>
                                    <select class="form-select" v-model="driverViolationFilters.status">
                                        <option value="">All</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Completed">Completed</option>
                                    </select>
                                </div>
                            </div>
                            <div class="d-flex justify-content-end mb-3">
                                <button class="btn btn-danger" @click="clearDriverViolationFilters">
                                    Clear Filters
                                </button>
                            </div>


                            <!-- Print Button -->
                            <div class="d-flex justify-content-end mb-3">
                                <button class="btn btn-primary" @click="printDriverViolationActions">
                                    Print Report
                                </button>
                            </div>

                            <!-- Violations Table -->
                            <div id="driverViolationsPrintSection">
                                <div class="table-responsive">
                                    <table class="table table-bordered table-hover">
                                        <thead class="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Bus</th>
                                                <th>Conductor</th>
                                                <th>Route</th>
                                                <th>Schedule</th>
                                                <th>Details</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(a, i) in filteredDriverViolations" :key="a.id">
                                                <td>{{ i + 1 }}</td>
                                                <td>{{ a.bus_registration }}</td>
                                                <td>{{ a.conductor_id }}</td>
                                                <td>{{ a.route }}</td>
                                                <td>{{ a.schedule }}</td>
                                                <td>
                                                    <ul class="list-unstyled mb-0" v-if="a.parameters">
                                                        <li v-for="(val, key) in a.parameters" :key="key">
                                                            <strong>{{ key }}:</strong> {{ val }}
                                                        </li>
                                                    </ul>
                                                </td>
                                                <td>
                                                    <span :class="`badge badge-${getStatusClass(a.status)}`">{{ a.status
                                                        }}</span>
                                                </td>
                                                <td>{{ formatDate(a.created_at) }}</td>
                                            </tr>
                                            <tr v-if="driverViolationActions.length === 0">
                                                <td colspan="8" class="text-center">No violations found for selected
                                                    filters</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div class="modal-footer">
                            <button class="btn btn-secondary" data-bs-dismiss="modal" @click="clearDriverViolationFilters">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="alert alert-danger text-center">
            <strong>Access Denied!</strong> You do not have permission to view this page.
        </div>
    </div>
</template>

<style scoped>
/* Modal styling */
.modal-xl {
    max-width: 90%;
}

/* Table styling 
.table-responsive {
    max-height: 70vh;
    overflow-y: auto;
}*/

/* .table th,
.table td {
    white-space: nowrap;
} */

.badge {
    padding: 0.25em 0.4em;
    font-size: 75%;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    vertical-align: baseline;
    border-radius: 0.25rem;
}

.badge-warning {
    background-color: #ffc107;
    color: #000;
}

.badge-success {
    background-color: #28a745;
    color: #fff;
}

.badge-info {
    background-color: #17a2b8;
    color: #fff;
}

.badge-secondary {
    background-color: #6c757d;
    color: #fff;
}

.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.badge-pending {
    background-color: #ffc107;
    color: #000;
}

.badge-completed {
    background-color: #28a745;
    color: #fff;
}

.badge-processing {
    background-color: #17a2b8;
    color: #fff;
}

.filter-row {
    margin-bottom: 15px;
}

.violation-summary {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 4px;
    margin-bottom: 20px;
}

.pagination {
    justify-content: center;
}

.page-link {
    cursor: pointer;
}
</style>

<script setup>
import { ref, onMounted, computed, watch, reactive } from "vue";
import { useRouter } from "vue-router";
import { useMeta } from "@/composables/use-meta";
import axios from 'axios';
import { BASE_URL, BASE_URL_3 } from '@/Config/config';
import Swal from 'sweetalert2';
import { useStore } from 'vuex';
import { Modal } from 'bootstrap';

useMeta({ title: "HR Violation Actions" });

const router = useRouter();
const store = useStore();

// Data variables
const violationActionsForHr = ref([]);
const isLoading = ref(false);
const drivers = ref([]);
const violations = ref([]);
const hrActions = ref([]);
const driverViolationActions = ref([]);
const selectedDriverId = ref(null);

// Search and filter variables
const searchValue = ref('');
const startDate = ref('');
const endDate = ref('');
const selectedViolationId = ref('');
const printStartDate = ref('');
const printEndDate = ref('');
const printShift = ref('');
const printSelectedViolationId = ref('');
const selectedShift = ref('');

// Pagination variables
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = computed(() => Math.ceil(filteredActions.value.length / itemsPerPage.value));

let printModal = null;
let driverViolationActionsModal = null;

const driverViolationFilters = reactive({
    startDate: '',
    endDate: '',
    shift: '',
    violationId: '',
    status: ''
});

// Permission check
const hasViewPermission = computed(() => {
    return store.getters.hasPermission('view_hr_actions');
});

// Alert functions
const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        timer: 3000
    });
};

// Get username from store
const username = computed(() => {
    const user = store.state.user;
    return user?.username || user?.name || 'User';
});

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-TZ', {
        style: 'currency',
        currency: 'TZS'
    }).format(amount);
};

const showPrintModal = () => {
    if (!printModal) {
        printModal = new Modal(document.getElementById('printPreviewModal'));
    }
    printModal.show();
};

const showDriverViolationActionsModal = async (driverId) => {
    selectedDriverId.value = driverId;
    try {
        await loadDriverViolationActions(driverId); // pass driverId
        if (!driverViolationActionsModal) {
            driverViolationActionsModal = new Modal(document.getElementById('viewDriverViolationActionsModal'));
        }
        driverViolationActionsModal.show();
    } catch (error) {
        console.error('Failed to load Violation Actions for the selected driver:', error);
        showErrorAlert('Failed to load Violation Actions for the selected driver!');
    }
};

const filteredPrintData = computed(() => {
    return hrActions.value.filter(action => {
        const actionDate = new Date(action.created_at);
        const start = printStartDate.value ? new Date(printStartDate.value) : null;
        const end = printEndDate.value ? new Date(printEndDate.value) : null;

        // Date range check
        const isInRange =
            (!start || actionDate >= start) &&
            (!end || actionDate <= end);

        // Shift check
        const schedule = action.violation_action.schedule || '';
        const timePart = schedule.split(' ')[0]; // "08:30"
        const shift = getShiftFromTime(timePart);

        const shiftMatch = !printShift.value || shift === printShift.value;

        // Violation type check (new)
        const violationMatch = !printSelectedViolationId.value ||
            action.violation_action.violation_id == printSelectedViolationId.value;

        return isInRange && shiftMatch && violationMatch;
    });
});

function getShiftFromTime(timeStr) {
    const [hour, minute] = timeStr.split(':').map(Number);
    const totalMinutes = hour * 60 + minute;

    const isDay = totalMinutes >= 510 && totalMinutes < 1230; // 08:30–20:30
    return isDay ? 'day' : 'night';
};

const parameterColumns = computed(() => {
    const keys = new Set();
    filteredPrintData.value.forEach(action => {
        const params = action.violation_action?.parameters || {};
        Object.keys(params).forEach(k => keys.add(k));
    });
    return Array.from(keys);
});

const paramColumns = computed(() => {
    const keys = new Set();
    filteredDriverViolations.value.forEach(action => {
        const params = action.parameters || {};
        Object.keys(params).forEach(k => keys.add(k));
    });
    return Array.from(keys);
});

const printFilteredHrActions = () => {
    setTimeout(() => {
        const tableContent = document.querySelector('#printPreviewModal table.table-bordered')?.cloneNode(true);
        if (!tableContent) return;

        const selectedViolation = violations.value.find(v => v.id == printSelectedViolationId.value);
        const reportTitle = selectedViolation
            ? `Violation Report: ${selectedViolation.title}`
            : 'Violation Report';

        const dateRangeText = printStartDate.value && printEndDate.value
            ? `From ${printStartDate.value} to ${printEndDate.value}`
            : '';

        const shiftText = printShift.value
            ? `Shift: ${printShift.value === 'day' ? 'Day' : 'Night'}`
            : '';

        const tableRows = filteredPrintData.value.map((action, index) => {
            const params = action.violation_action?.parameters || {};
            const paramCols = parameterColumns.value.map(k => `<td>${params[k] ?? '—'}</td>`).join('');
            return `
        <tr>
            <td>${index + 1}</td>
            <td>${action.violation_action?.conductor_id ?? '—'}</td>
            <td>${action.violation_action?.bus_registration ?? '—'}</td>
            <td>${action.violation_action?.route ?? '—'}</td>
            <td>${action.violation_action?.schedule ?? '—'}</td>
            ${paramCols}
            <td>${formatCurrency(action.consolidated_amount) || '—'}</td>
            <td>${action.remarks || '—'}</td> 
        </tr>`;
        }).join('');

        const tableHeader = `
    <thead>
        <tr>
            <th>#</th>
            <th>Conductor</th>
            <th>Bus</th>
            <th>Route</th>
            <th>Schedule</th>
            ${parameterColumns.value.map(k => `<th>${k}</th>`).join('')}
            <th>Consolidated Amount</th>
            <th>Remarks</th>
        </tr>
    </thead>`;
        tableContent.innerHTML = `
            ${tableHeader}
            <tbody>
                ${tableRows}
            </tbody>
        `;

        const printWindow = window.open('', '', 'width=800,height=900');
        if (!printWindow) return;

        printWindow.document.write(`
            <html>
                <head>
                    <title>${reportTitle}</title>
                    <style>
                        @page { size: A4 landscape; margin: 10pt; }
                        body { font-family: Arial, sans-serif; font-size: 12px; padding: 15px; }
                        .header { text-align: center; margin-bottom: 20px; }
                        .header .title { font-size: 24px; font-weight: bold; color: red; }
                        .sub-header { display: flex; justify-content: space-between; margin-bottom: 10px; }
                        .report-title { font-size: 18px; font-weight: bold; text-decoration: underline; }
                        .date-range { font-weight: bold; font-size: 14px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #000 !important; padding: 8px; text-align: left; }
                        td { font-size: 12px; }
                        th { background-color: #f8f9fa !important; font-weight: bold; }
                        .footer { display: flex; justify-content: space-between; align-items: center; margin-top: 40px; border-top: 2px solid #000; padding-top: 10px; font-size: 12px; flex-wrap: wrap; gap: 10px; }
                        .footer > div { min-width: 18%; }
                        .print-actions { display: flex; justify-content: center; margin: 20px 0; gap: 10px; }
                        button { padding: 8px 16px; border: none; border-radius: 4px; cursor: pointer; font-weight: bold; }
                        .btn-print { background-color: #4CAF50; color: white; }
                        .btn-pdf { background-color: #2196F3; color: white; }
                        .btn-close { background-color: #f44336; color: white; }
                        @media print { .print-actions { display: none !important; } }
                        tr { page-break-inside: avoid; }
                    </style>
                    <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"><\/script>
                </head>
                <body>
                    <div id="content-to-export">
                        <div class="header">
                            <div class="title">ABOOD BUS SERVICE LIMITED</div>
                            <div>P.O Box 127 Msamvu Morogoro, Tanzania</div>
                            <div>Mob: +255 (0) 23 261 3589</div>
                        </div>
                        <div class="sub-header">
                            <div class="report-title">${reportTitle}</div>
                            <div class="date-range">${dateRangeText} ${shiftText ? `| ${shiftText}` : ''}</div>
                        </div>
                        <div class="content">
                            <table class="table table-bordered">
                                ${tableHeader}
                                <tbody>${tableRows}</tbody>
                            </table>
                        </div>

                        <div class="footer">
                            <div>Prepared By: ${username.value}<br>IT Officer</div>
                            <div>____________________<br>HR</div>
                            <div>_____________________<br>Accounts</div>
                            <div>_____________________<br>GM/MD</div>
                        </div>
                    </div>
                    <div class="print-actions">
                        <button onclick="window.print()" class="btn-print">Print Report</button>
                        <button onclick="downloadPDF()" class="btn-pdf">Download as PDF</button>
                        <button onclick="window.close()" class="btn-close">Close</button>
                    </div>
                    <script>
                        function downloadPDF() {
                            const element = document.getElementById('content-to-export');
                            const opt = {
                                margin: 10,
                                filename: 'hr_violation_report.pdf',
                                image: { type: 'jpeg', quality: 0.98 },
                                html2canvas: { scale: 2, logging: true, useCORS: true },
                                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
                            };
                            const originalText = document.querySelector('.btn-pdf').textContent;
                            document.querySelector('.btn-pdf').textContent = 'Generating PDF...';
                            html2pdf().set(opt).from(element).save().then(() => {
                                document.querySelector('.btn-pdf').textContent = originalText;
                            });
                        }
                    <\/script>
                </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();
    }, 100);
};

const printDriverViolationActions = () => {
    setTimeout(() => {
        const actions = filteredDriverViolations.value;

        const selectedViolation = violations.value.find(v => v.id == driverViolationFilters.violationId);
        const reportTitle = selectedViolation
            ? `Violation Report: ${selectedViolation.title}`
            : 'Violation Report';

        const dateRangeText = driverViolationFilters.startDate && driverViolationFilters.endDate
            ? `From ${driverViolationFilters.startDate} to ${driverViolationFilters.endDate}`
            : '';

        const shiftText = driverViolationFilters.shift
            ? `Shift: ${driverViolationFilters.shift === 'day' ? 'Day' : 'Night'}`
            : '';

        const rows = actions.map((a, i) => {
            const params = a.parameters || {};
            // const details = Object.entries(params).map(([k, v]) => `<strong>${k}:</strong> ${v}`).join('<br>');
            const details = paramColumns.value.map(k => `<td>${params[k] ?? '—'}</td>`).join('');


            return `
                <tr>
                    <td>${i + 1}</td>
                    <td>${a.bus_registration}</td>
                    <td>${a.conductor_id}</td>
                    <td>${a.route}</td>
                    <td>${a.schedule}</td>
                    ${details}
                </tr>
            `;
        }).join('');

        const html = `
            <html>
                <head>
                    <title>Driver Violations</title>
                    <style>
                        @page { size: A4 landscape; margin: 10pt; }
                        body { font-family: Arial, sans-serif; font-size: 12px; padding: 15px; }
                        .header { text-align: center; margin-bottom: 20px; }
                        .header .title { font-size: 24px; font-weight: bold; color: red; }
                        .sub-header { display: flex; justify-content: space-between; margin-bottom: 10px; }
                        .report-title { font-size: 18px; font-weight: bold; text-decoration: underline; }
                        .date-range { font-weight: bold; font-size: 14px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #000 !important; padding: 8px; text-align: left; }
                        td { font-size: 12px; }
                        th { background-color: #f8f9fa !important; font-weight: bold; }
                        .footer { display: flex; justify-content: space-between; align-items: center; margin-top: 40px; border-top: 2px solid #000; padding-top: 10px; font-size: 12px; flex-wrap: wrap; gap: 10px; }
                        .footer > div { min-width: 18%; }
                        .print-actions { display: flex; justify-content: center; margin: 20px 0; gap: 10px; }
                        button { padding: 8px 16px; border: none; border-radius: 4px; cursor: pointer; font-weight: bold; }
                        .btn-print { background-color: #4CAF50; color: white; }
                        .btn-pdf { background-color: #2196F3; color: white; }
                        .btn-close { background-color: #f44336; color: white; }
                        @media print { .print-actions { display: none !important; } }
                        tr { page-break-inside: avoid; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div class="title">ABOOD BUS SERVICE LIMITED</div>
                        <div>P.O Box 127 Msamvu Morogoro, Tanzania</div>
                        <div>Mob: +255 (0) 23 261 3589</div>
                    </div>
                    <div class="sub-header">
                        <div class="report-title">${reportTitle}</div>
                        <div class="date-range">${dateRangeText} ${shiftText ? `| ${shiftText}` : ''}</div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Bus</th>
                                <th>Conductor</th>
                                <th>Route</th>
                                <th>Schedule</th>
                                ${paramColumns.value.map(k => `<th>${k}</th>`).join('')}
                            </tr>
                        </thead>
                        <tbody>
                            ${rows || '<tr><td colspan="8">No data found</td></tr>'}
                        </tbody>
                    </table>

                    <div class="footer">
                        <div>Prepared By: ${username.value}<br>IT Officer</div>
                        <div>____________________<br>HR</div>
                        <div>_____________________<br>Accounts</div>
                        <div>_____________________<br>GM/MD</div>
                    </div>
                    </div>
                    <div class="print-actions">
                        <button onclick="window.print()" class="btn-print">Print Report</button>
                        <button onclick="window.close()" class="btn-close">Close</button>
                    </div>
                </body>
            </html>
        `;

        const printWin = window.open('', '', 'width=900,height=700');
        if (!printWin) return;
        printWin.document.write(html);
        printWin.document.close();
        printWin.focus();
        printWin.print();
    }, 100);
};

const getViolationTitle = (violationId) => {
    const violation = violations.value.find(v => v.id === violationId);
    return violation ? violation.title : 'N/A';
};

// Add shift information to actions
const actionsWithShifts = computed(() => {
    return violationActionsForHr.value.map(action => {
        if (!action.created_at) return { ...action, shift: 'unknown' };

        const actionTime = new Date(action.created_at);
        const hours = actionTime.getHours();
        const minutes = actionTime.getMinutes();
        const totalMinutes = hours * 60 + minutes;

        let shift;
        if (totalMinutes >= 510 && totalMinutes < 1230) {
            shift = 'day';
        } else {
            shift = 'night';
        }

        return { ...action, shift };
    });
});

// Add shift information to actions
const driverViolationActionsShifts = computed(() => {
    return driverViolationActions.value.map(action => {
        if (!action.created_at) return { ...action, shift: 'unknown' };

        const actionTime = new Date(action.created_at);
        const hours = actionTime.getHours();
        const minutes = actionTime.getMinutes();
        const totalMinutes = hours * 60 + minutes;

        let shift;
        if (totalMinutes >= 510 && totalMinutes < 1230) {
            shift = 'day';
        } else {
            shift = 'night';
        }

        return { ...action, shift };
    });
});

// Count violations by shift
const shiftCounts = computed(() => {
    const counts = {
        day: 0,
        night: 0,
        all: actionsWithShifts.value.length
    };

    actionsWithShifts.value.forEach(action => {
        if (action.shift === 'day') counts.day++;
        if (action.shift === 'night') counts.night++;
    });

    return counts;
});

// Count violations by type
const violationCounts = computed(() => {
    const counts = {};

    violations.value.forEach(violation => {
        counts[violation.id] = {
            title: violation.title,
            count: 0
        };
    });

    counts.all = {
        title: 'All Violations',
        count: violationActionsForHr.value.length
    };

    violationActionsForHr.value.forEach(action => {
        if (counts[action.violation_id]) {
            counts[action.violation_id].count++;
        }
    });

    return counts;
});

// Filtered violation counts
const filteredViolationCounts = computed(() => {
    const counts = {};

    violations.value.forEach(violation => {
        counts[violation.id] = {
            title: violation.title,
            count: 0
        };
    });

    counts.all = {
        title: 'All Violations',
        count: 0
    };

    filteredActions.value.forEach(action => {
        counts.all.count++;
        if (counts[action.violation_id]) {
            counts[action.violation_id].count++;
        }
    });

    return counts;
});

// Filter actions based on search and filters
const filteredActions = computed(() => {
    let filtered = actionsWithShifts.value;

    // Filter by shift
    if (selectedShift.value) {
        filtered = filtered.filter(action => action.shift === selectedShift.value);
    }

    // Filter by violation
    if (selectedViolationId.value) {
        filtered = filtered.filter(action => action.violation_id == selectedViolationId.value);
    }

    // Filter by date range
    if (startDate.value && endDate.value) {
        filtered = filtered.filter(action => {
            const actionDate = action.created_at ? new Date(action.created_at) : null;
            if (!actionDate) return false;
            return (
                actionDate >= new Date(startDate.value) &&
                actionDate <= new Date(endDate.value + 'T23:59:59')
            );
        });
    }

    // Filter by search term
    if (searchValue.value) {
        const keyword = searchValue.value.toLowerCase();
        filtered = filtered.filter(action => {
            return (
                (action.driver_id || '').toString().toLowerCase().includes(keyword) ||
                (action.bus_registration || '').toLowerCase().includes(keyword) ||
                (action.route || '').toLowerCase().includes(keyword) ||
                (action.conductor_id || '').toLowerCase().includes(keyword) ||
                (action.user?.username || '').toLowerCase().includes(keyword)
            );
        });
    }

    return filtered;
});

// Paginated data
const paginatedActions = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredActions.value.slice(start, end);
});

// Pagination methods
const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const firstPage = () => {
    currentPage.value = 1;
};

const lastPage = () => {
    currentPage.value = totalPages.value;
};

// Reset to first page when filters change
watch([searchValue, startDate, endDate, selectedViolationId], () => {
    currentPage.value = 1;
});

// Format date for display
const formatDate = (dateString) => {
    if (!dateString) return '—';
    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    return new Date(dateString).toLocaleString(undefined, options);
};

// Get status class for badge
const getStatusClass = (status) => {
    const statusMap = {
        'Pending': 'pending',
        'Completed': 'completed',
        'Processing': 'processing'
    };
    return statusMap[status] || 'secondary';
};

// Get driver name from ID
const getDriverName = (driverId) => {
    const idToFind = typeof driverId === 'string' ? parseInt(driverId) : driverId;
    const driver = drivers.value.find(d => d.id === idToFind);
    return driver?.name || driverId || '—';
};

// Load violation data
const loadViolationActionsForHR = async () => {
    try {
        isLoading.value = true;
        const response = await axios.get(`${BASE_URL}/abs-violation-actions/hr-actions/get`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violationActionsForHr.value = response.data.data || response.data;
    } catch (err) {
        console.error("Failed to load violation actions for HR:", err);
        showErrorAlert(err.response?.data?.message || 'Failed to load violation actions for HR');
    } finally {
        isLoading.value = false;
    }
};

// Load drivers
const loadDrivers = async () => {
    try {
        const response = await axios.get(`${BASE_URL_3}/company/employees/list?company_id=2`);
        drivers.value = response.data.employees || response.data;
        console.log('Drivers API response:', response.status);
    } catch (error) {
        console.error('Error fetching employees:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to load drivers');
    }
};

// Load violations
const loadViolations = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/violations`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violations.value = response.data.data || response.data;
    } catch (error) {
        console.error('Error fetching violations:', error);
    }
};

const loadHrActions = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/abs-violation-actions/hr-actions/index`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        hrActions.value = response.data.data;
        console.log('HR Actions API Response', response.status);
    } catch (error) {
        console.error('Error fetching HR actions:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to load HR actions');
    }
}

const loadDriverViolationActions = async (driverId) => {
    try {
        const response = await axios.get(`${BASE_URL}/abs-violation-actions/employee/${driverId}`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        driverViolationActions.value = response.data.data || response.data;
    } catch (error) {
        console.error('Error fetching driver violation actions:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to load driver violation actions');
    }
};

const filteredDriverViolations = computed(() => {
    return driverViolationActionsShifts.value.filter(action => {
        const createdAt = new Date(action.created_at);

        // Date range
        if (driverViolationFilters.startDate) {
            const start = new Date(driverViolationFilters.startDate);
            if (createdAt < start) return false;
        }

        if (driverViolationFilters.endDate) {
            const end = new Date(driverViolationFilters.endDate);
            if (createdAt > end) return false;
        }

        // Shift filter
        if (driverViolationFilters.shift && action.shift !== driverViolationFilters.shift) {
            return false;
        }

        // Violation type filter
        if (driverViolationFilters.violationId && action.violation_id !== driverViolationFilters.violationId) {
            return false;
        }

        // Status filter
        if (driverViolationFilters.status && action.status !== driverViolationFilters.status) {
            return false;
        }

        return true;
    });
});

// View details
const viewDetails = (id) => {
    router.push(`/abs/violation/action/hr-actions/${id}`);
};

// Clear all filters
const clearFilters = () => {
    startDate.value = '';
    endDate.value = '';
    selectedViolationId.value = '';
    selectedShift.value = '';
    searchValue.value = '';
};

// Clear Modal Filters
const clearPrintFilters = () => {
    printStartDate.value = '';
    printEndDate.value = '';
    printShift.value = '';
    printSelectedViolationId.value = '';
};

const clearDriverViolationFilters = () => {
    driverViolationFilters.startDate = '';
    driverViolationFilters.endDate = '';
    driverViolationFilters.shift = '';
    driverViolationFilters.violationId = '';
    driverViolationFilters.status = '';
};

// Initialize component
onMounted(async () => {
    try {
        isLoading.value = true;
        await Promise.all([
            loadViolationActionsForHR(),
            loadDrivers(),
            loadViolations(),
            loadHrActions()
        ]);
    } catch (err) {
        console.error("Failed to load data:", err);
        showErrorAlert(err.response?.data?.message || 'Failed to load data');
    } finally {
        isLoading.value = false;
    }
});
</script>